/**
 * TEXT_COLORS — Named color palette for text slots
 * Single source of truth for text color options
 */

export interface TextColorDefinition {
  label: string;
  value: string; // hex color (e.g., "#333333") or CSS variable
}

/**
 * Predefined text colors registry.
 * Colors can be referenced by ID (e.g., "primary-text") or used directly as hex/rgb.
 */
export const TEXT_COLORS: Record<string, TextColorDefinition> = {
  'foreground': {
    label: 'Foreground',
    value: 'var(--color-foreground)', // CSS var for theme integration
  },
  'primary-text': {
    label: 'Primary',
    value: '#1a1a2e',
  },
  'secondary-text': {
    label: 'Secondary',
    value: '#4a4a68',
  },
  'muted-text': {
    label: 'Muted',
    value: '#6b7280',
  },
  'accent': {
    label: 'Accent',
    value: '#3b82f6', // blue-500
  },
  'success': {
    label: 'Success',
    value: '#22c55e', // green-500
  },
  'warning': {
    label: 'Warning',
    value: '#f59e0b', // amber-500
  },
  'danger': {
    label: 'Danger',
    value: '#ef4444', // red-500
  },
};

/**
 * Get text color by ID.
 */
export function getTextColorById(id: string): TextColorDefinition | undefined {
  return TEXT_COLORS[id];
}

/**
 * Get all text color options (for picker components).
 * Returns array with ID included for easy mapping.
 */
export function getTextColorOptions(): Array<TextColorDefinition & { id: string }> {
  return Object.entries(TEXT_COLORS).map(([id, def]) => ({ ...def, id }));
}

/**
 * Validate if a color value is valid (hex, rgb, or named from registry).
 * 
 * @param color Color string to validate
 * @returns true if color is valid format or named color ID
 */
export function isValidTextColor(color: string): boolean {
  if (!color || typeof color !== 'string') return false;
  
  // Named color from registry
  if (TEXT_COLORS[color]) return true;
  
  // Hex color (#RGB or #RRGGBB)
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color)) return true;
  
  // RGB/RGBA
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(color)) return true;
  
  // CSS variable (allowed for theme integration)
  if (/^var\(--[\w-]+\)$/.test(color)) return true;
  
  return false;
}

/**
 * Resolve a color value to its CSS value.
 * - Named color → lookup from registry
 * - Hex/RGB/CSS var → return as-is
 * - Invalid → return fallback
 * 
 * @param color Color string (named ID, hex, rgb, or CSS var)
 * @param fallback Fallback color if color is invalid or undefined
 * @returns CSS color value
 */
export function resolveTextColor(color: string | undefined, fallback = 'inherit'): string {
  if (!color) return fallback;
  
  // Named color
  const named = TEXT_COLORS[color];
  if (named) return named.value;
  
  // Valid direct color
  if (isValidTextColor(color)) return color;
  
  // Invalid, return fallback
  return fallback;
}
